import { TOKEN_STORAGE_KEY, reportRefusal, sessionRenewal } from '@h2hspark/auth-ui';
import { createApiRequest } from '@h2hspark/ui';

import { getWebEnvironment } from '@/env';

/**
 * The member app's single HTTP entry point. Transport lives in `@h2hspark/ui`; all this
 * app supplies is where its API is.
 */
export const apiRequest = createApiRequest({
  resolveBaseUrl: () => getWebEnvironment().apiUrl,
  resolveAuthToken,
  // **Renewal on use** (`ADR 051` §2). The transport reads the access token's own `exp` before
  // it sends and renews when it has run out, single-flight across every query that finds it
  // expired in the same tick; a 401 that arrives anyway gets one renewal and one retry. Nothing
  // renewed at all before this — a member was signed out 60 minutes after signing in, absolute,
  // against `USR-007` AC-2's thirty days *of inactivity*.
  renewal: sessionRenewal,
  // A refusal is reported with the code the API sent, and `reportRefusal` decides what it means
  // (`PLT-014` AC-7): `ACCOUNT_SUSPENDED` ends the session there and then, with a reason the
  // sign-in screen shows; anything else asks `/auth/me`, which is the authority — see
  // `session-end.ts` and `session-recheck.ts`. Without the second half an expired session left
  // every page on its loading skeletons until the browser was refreshed. A suspended *member* is
  // not refused with that code — `USR-012` AC-8 keeps that answer indistinguishable — so here it
  // reaches an administrator whose roster row was suspended while they were using the member app.
  onUnauthenticated: reportRefusal,
});

/**
 * Reads the bearer token auth-ui stores after login. Supplied to the shared
 * transport from the app side so `@h2hspark/ui` stays independent of the auth
 * package. localStorage may be unavailable (private mode); treat that as
 * signed-out rather than an error.
 */
function resolveAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}
